import { AccrualLineAccountMode } from "@common/helpers/accrualLineAccountTypes";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import classNames from "classnames";
import {
    ChangedCells,
    SpreadsheetGrid,
    SpreadsheetGridHandle,
} from "@components/spreadsheet/SpreadsheetGrid";
import { SpreadsheetData } from "@components/spreadsheet/types";
import { appendSpreadsheetActionsColumn } from "@components/spreadsheet/spreadsheetActionsColumn";
import spreadsheetStyles from "@components/spreadsheet/SpreadsheetGrid.module.scss";
import { SpreadsheetColumnSettings } from "@components/spreadsheet/SpreadsheetColumnSettings";
import { useSpreadsheetColumnVisibility } from "@components/spreadsheet/useSpreadsheetColumnVisibility";
import { useSpreadsheetColumnOrder } from "@components/spreadsheet/useSpreadsheetColumnOrder";
import { CloseIcon } from "@icons";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useLedgers } from "@api/ledger.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { findPrimaryLedgerId } from "@lib/accountOverrides";
import {
    NativeInvoiceAdjustmentMode,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";
import { formatMoney } from "./formatMoney";
import {
    buildCatalogItemLinePatch,
    CatalogItemSelection,
} from "./catalogItemSelection.helpers";
import {
    INVOICE_LINE_COLUMNS,
    INVOICE_LINE_ITEMS_DEFAULT_HIDDEN_FIELDS,
    INVOICE_LINE_ITEMS_SPREADSHEET_COLUMNS_STORAGE_KEY,
    LINE_ACTIONS_COLUMN_WIDTH_PX,
    buildInvoiceLineGridTemplateColumns,
    isInvoiceLineColumnAvailableInViews,
} from "./invoiceLineItemsSpreadsheetColumns";
import { buildLineItemColumns } from "./buildLineItemColumns";
import {
    computeNativeInvoiceLineAmount,
    computeNativeInvoiceTotals,
    computeNativeInvoiceWeightedAveragePercent,
} from "./computeNativeInvoiceTotals";
import { buildLinePatch, LineItemSpreadsheetRow } from "./lineItemPatchHelpers";
import { TotalsAmountRow, TotalsTextRow } from "./LineItemsTotalsRows";
import {
    convertNativeInvoicePerLineAdjustmentMode,
    NativeInvoicePerLineKind,
} from "./nativeInvoicePerLineAdjustment";
import { useNativeInvoiceFormContext } from "./NativeInvoiceFormContext";
import styles from "./SidePanel.module.scss";

const DEFAULT_COLUMN_ORDER: readonly string[] = INVOICE_LINE_COLUMNS.map(
    (column) => column.field,
);

interface Props {
    lines: NativeInvoiceLineRow[];
    entityId?: number;
    subtotal: number;
    total: number;
    onChangeLine: (
        lineId: string,
        patch: Partial<NativeInvoiceLineRow>,
    ) => void;
    onAddLine: () => void;
    onRemoveLine: (lineId: string) => void;
    suggestedAccountLineIds?: readonly string[];
    onConfirmAccountSuggestion?: (lineId: string) => void;
    suggestedItemLineIds?: readonly string[];
    onConfirmItemSuggestion?: (lineId: string) => void;
    accrualMode?: AccrualLineAccountMode;
    isAccountReviewHighlighted?: boolean;
    isItemReviewHighlighted?: boolean;
}

export const LineItemsSection: React.FC<Props> = ({
    lines,
    entityId,
    subtotal,
    total,
    onChangeLine,
    onAddLine,
    onRemoveLine,
    suggestedAccountLineIds,
    onConfirmAccountSuggestion,
    suggestedItemLineIds,
    onConfirmItemSuggestion,
    accrualMode,
    isAccountReviewHighlighted,
    isItemReviewHighlighted,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const {
        tax,
        discount,
        deposit,
        taxMode,
        discountMode,
        depositMode,
        isTaxPerLineItem,
        isDiscountPerLineItem,
        isDepositPerLineItem,
    } = state;

    // Tax/discount/deposit are rarely used, so the rows (and the redundant
    // Subtotal) stay hidden behind an "Add tax, discount or deposit" action
    // unless the invoice already carries one of them.
    const hasAdjustments =
        tax !== 0 ||
        discount !== 0 ||
        deposit !== 0 ||
        isTaxPerLineItem ||
        isDiscountPerLineItem ||
        isDepositPerLineItem;
    const [isAdjustmentsRevealed, setIsAdjustmentsRevealed] = useState(false);
    const shouldShowAdjustments = isAdjustmentsRevealed || hasAdjustments;

    const gridHandleRef = useRef<SpreadsheetGridHandle>(null);

    // Class requires the CLASSES plan capability. Income account is always
    // editable. Receivables requires ACCRUAL_LEDGER and a primary accrual
    // ledger on the selected entity — the saved AR override targets that
    // ledger, so the column stays hidden when the pick would be dropped.
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const { hasAccess: hasAccrualLedgerAccess } = useAllowFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );
    const { data: ledgers } = useLedgers(entityId);
    const selectedEntityHasAccrualLedger = useMemo(
        () =>
            findPrimaryLedgerId(ledgers, AccountingLedgerBasis.accruals) !=
            null,
        [ledgers],
    );
    const showClassColumn = hasClassesAccess;
    const showReceivablesAccountColumn =
        hasAccrualLedgerAccess && selectedEntityHasAccrualLedger;

    const { taxAmount, discountAmount, depositAmount } = useMemo(
        () =>
            computeNativeInvoiceTotals({
                lines,
                tax,
                taxMode,
                discount,
                discountMode,
                deposit,
                depositMode,
                isTaxPerLineItem,
                isDiscountPerLineItem,
                isDepositPerLineItem,
            }),
        [
            lines,
            tax,
            taxMode,
            discount,
            discountMode,
            deposit,
            depositMode,
            isTaxPerLineItem,
            isDiscountPerLineItem,
            isDepositPerLineItem,
        ],
    );
    const taxPercent = computeNativeInvoiceWeightedAveragePercent({
        lines,
        field: "tax",
    });
    const discountPercent = computeNativeInvoiceWeightedAveragePercent({
        lines,
        field: "discount",
    });
    const depositPercent = computeNativeInvoiceWeightedAveragePercent({
        lines,
        field: "deposit",
    });

    const handlePerLineModeChange = useCallback(
        (kind: NativeInvoicePerLineKind, mode: NativeInvoiceAdjustmentMode) => {
            setState((prev) =>
                convertNativeInvoicePerLineAdjustmentMode({
                    state: prev,
                    kind,
                    mode,
                }),
            );
        },
        [setState],
    );

    const {
        hiddenFields,
        toggleField,
        reset: resetColumnVisibility,
    } = useSpreadsheetColumnVisibility(
        INVOICE_LINE_ITEMS_DEFAULT_HIDDEN_FIELDS,
        INVOICE_LINE_ITEMS_SPREADSHEET_COLUMNS_STORAGE_KEY,
    );

    const {
        orderedFields,
        moveField,
        reset: resetColumnOrder,
        hasCustomOrder,
    } = useSpreadsheetColumnOrder(
        DEFAULT_COLUMN_ORDER,
        `${INVOICE_LINE_ITEMS_SPREADSHEET_COLUMNS_STORAGE_KEY}-order`,
    );

    const handleResetColumns = useCallback(() => {
        resetColumnVisibility();
        resetColumnOrder();
    }, [resetColumnVisibility, resetColumnOrder]);

    // Column metas in the user's display order; feeds the grid, the template
    // tracks, and the settings popover so they never disagree.
    const orderedLineColumns = useMemo(
        () =>
            [...INVOICE_LINE_COLUMNS].sort(
                (a, b) =>
                    orderedFields.indexOf(a.field) -
                    orderedFields.indexOf(b.field),
            ),
        [orderedFields],
    );

    // Settings popover lists every column the grid can currently render.
    // Feature-gated columns — Class, receivables, and per-line
    // Tax / Discount / Deposit — are omitted entirely so they never appear
    // as toggles when unavailable (matching JE drawer Class behavior).
    const columnSettingsColumns = useMemo(
        () =>
            orderedLineColumns.filter((column) =>
                isInvoiceLineColumnAvailableInViews(column.field, {
                    showClassColumn,
                    showReceivablesAccountColumn,
                    isTaxPerLineItem,
                    isDiscountPerLineItem,
                    isDepositPerLineItem,
                }),
            ),
        [
            orderedLineColumns,
            showClassColumn,
            showReceivablesAccountColumn,
            isTaxPerLineItem,
            isDiscountPerLineItem,
            isDepositPerLineItem,
        ],
    );
    const lineRows = useMemo<SpreadsheetData>(
        () =>
            lines.map((line): LineItemSpreadsheetRow => ({
                id: line.id,
                productOrService: line.productOrService,
                description: line.description,
                quantity: line.quantity,
                unitAmount: line.unitAmount,
                amount: computeNativeInvoiceLineAmount(line),
                classIds: line.classIds,
                accrualAccountId: line.accrualAccountId,
                receivablesAccountId: line.receivablesAccountId,
                tax: line.tax,
                discount: line.discount,
                deposit: line.deposit,
            })),
        [lines],
    );

    // Selecting/creating an item writes all derived fields to the line in one
    // patch (by line id), preserving line identity for the snapshot sync. The
    // picker also commits the name through the grid's cell-commit path, so
    // remember the selection to recognize (and skip) that echo in
    // handleLineChange — otherwise the manual-edit detach would null the
    // backlink that was just attached.
    const lastSelectionRef = useRef<{ lineId: string; name: string } | null>(
        null,
    );
    const handleSelectItem = useCallback(
        (lineId: string, selection: CatalogItemSelection) => {
            lastSelectionRef.current = {
                lineId,
                name: selection.productOrService,
            };
            onChangeLine(lineId, buildCatalogItemLinePatch(selection));
        },
        [onChangeLine],
    );

    const allColumns = useMemo(
        () =>
            buildLineItemColumns({
                entityId,
                currency: "usd",
                lines,
                onSelectCatalogItem: handleSelectItem,
                showClassColumn,
                showReceivablesAccountColumn,
                showTaxColumn: isTaxPerLineItem,
                showDiscountColumn: isDiscountPerLineItem,
                showDepositColumn: isDepositPerLineItem,
                taxMode,
                discountMode,
                depositMode,
                suggestedAccountLineIds,
                onConfirmAccountSuggestion,
                suggestedItemLineIds,
                onConfirmItemSuggestion,
                accrualMode,
                isAccountReviewHighlighted,
            }),
        [
            entityId,
            lines,
            handleSelectItem,
            showClassColumn,
            showReceivablesAccountColumn,
            isTaxPerLineItem,
            isDiscountPerLineItem,
            isDepositPerLineItem,
            taxMode,
            discountMode,
            depositMode,
            suggestedAccountLineIds,
            onConfirmAccountSuggestion,
            suggestedItemLineIds,
            onConfirmItemSuggestion,
            accrualMode,
            isAccountReviewHighlighted,
        ],
    );

    const columns = useMemo(() => {
        const visibleColumns = hiddenFields.size
            ? allColumns.filter((column) => !hiddenFields.has(column.field))
            : allColumns;
        return [...visibleColumns].sort(
            (a, b) =>
                orderedFields.indexOf(a.field) - orderedFields.indexOf(b.field),
        );
    }, [allColumns, hiddenFields, orderedFields]);

    const addLineWithItemFocus = useCallback(() => {
        const itemColumnIndex = columns.findIndex(
            (column) => column.field === "productOrService",
        );
        const row = lines.length;
        flushSync(() => {
            onAddLine();
        });
        gridHandleRef.current?.focus({
            row,
            col: Math.max(itemColumnIndex, 0),
        });
    }, [columns, lines.length, onAddLine]);

    useKeyboardCommand({
        keys: "shift+enter",
        callback: addLineWithItemFocus,
        options: { description: "Add line item" },
        dependencies: [addLineWithItemFocus],
    });

    const gridTemplateColumns = useMemo(() => {
        const visibleMeta = orderedLineColumns.filter((meta) =>
            columns.some((column) => column.field === meta.field),
        );
        return buildInvoiceLineGridTemplateColumns(visibleMeta);
    }, [columns, orderedLineColumns]);

    // Lives in the pinned actions-column header so it stays visible during
    // horizontal scroll.
    const columnSettingsTrigger = useMemo(
        () => (
            <div className={styles.columnSettingsHeaderButton}>
                <SpreadsheetColumnSettings
                    columns={columnSettingsColumns}
                    hiddenFields={hiddenFields}
                    defaultHiddenFields={
                        INVOICE_LINE_ITEMS_DEFAULT_HIDDEN_FIELDS
                    }
                    onToggle={toggleField}
                    onReset={handleResetColumns}
                    onReorder={moveField}
                    hasCustomOrder={hasCustomOrder}
                    triggerVariant="secondary"
                    triggerSize="sm"
                />
            </div>
        ),
        [
            columnSettingsColumns,
            hiddenFields,
            toggleField,
            handleResetColumns,
            moveField,
            hasCustomOrder,
        ],
    );

    const gridColumns = useMemo(
        () =>
            appendSpreadsheetActionsColumn(columns, {
                label: columnSettingsTrigger,
                width: LINE_ACTIONS_COLUMN_WIDTH_PX,
                isPinnedRight: true,
                cellFn: ({ row }) => {
                    const lineId = row.id;
                    if (typeof lineId !== "string" || !lineId) {
                        return null;
                    }
                    return (
                        <button
                            type="button"
                            className={classNames(
                                styles.removeLineButton,
                                spreadsheetStyles.showOnRowHover,
                            )}
                            onClick={() => onRemoveLine(lineId)}
                        >
                            <CloseIcon />
                        </button>
                    );
                },
            }),
        [columns, columnSettingsTrigger, onRemoveLine],
    );

    const handleLineChange = useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            // Group changes by row so all field changes from a single batch
            // (paste/drag-fill/clear) land in one patch.
            const patchesByRow = new Map<
                number,
                Partial<NativeInvoiceLineRow>
            >();

            for (const [rowIdx, colIdx] of changedCells) {
                const row = newData[rowIdx];
                if (!row) {
                    continue;
                }
                const original = lines[rowIdx];
                if (!original) {
                    continue;
                }
                // Derive the field from the column defs so the mapping cannot
                // drift when columns are added or reordered.
                const field = columns[colIdx]?.field;
                if (!field || field === "amount") {
                    continue;
                }
                const value = row[field];
                if (field === "productOrService") {
                    const lastSelection = lastSelectionRef.current;
                    if (
                        lastSelection &&
                        lastSelection.lineId === original.id &&
                        lastSelection.name === value
                    ) {
                        // Echo of a picker selection: the line was already
                        // patched with the catalog fields; don't detach.
                        lastSelectionRef.current = null;
                        continue;
                    }
                    if (value === original.productOrService) {
                        // Editor closed with the name unchanged; a no-op
                        // commit must not detach the catalog backlink.
                        continue;
                    }
                }
                const patch = buildLinePatch(field, value);
                if (patch) {
                    const existing = patchesByRow.get(rowIdx) ?? {};
                    patchesByRow.set(rowIdx, { ...existing, ...patch });
                }
            }

            // Apply accumulated patches once per row, seeing all field
            // changes that happened together.
            for (const [rowIdx, patch] of patchesByRow) {
                const original = lines[rowIdx];
                if (original) {
                    onChangeLine(original.id, patch);
                }
            }
        },
        [onChangeLine, lines, columns],
    );

    // The spreadsheet grid stops propagation of nearly every keydown, so the
    // document-level shift+enter hotkey below never fires while the grid (or a
    // cell editor) has focus. Capturing here runs before the grid's own
    // handler, keeping the shortcut available anywhere in the table.
    const handleTableKeyDownCapture = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (
                event.key === "Enter" &&
                event.shiftKey &&
                !event.metaKey &&
                !event.ctrlKey &&
                !event.altKey
            ) {
                event.preventDefault();
                event.stopPropagation();
                addLineWithItemFocus();
            }
        },
        [addLineWithItemFocus],
    );

    return (
        <div
            className={styles.lineItemsStack}
            onKeyDownCapture={handleTableKeyDownCapture}
        >
            <div className={styles.lineItemsTable}>
                <div className={styles.gridWrapper}>
                    <SpreadsheetGrid
                        gridHandle={gridHandleRef}
                        data={lineRows}
                        columns={gridColumns}
                        gridTemplateColumns={gridTemplateColumns}
                        rowClassName={styles.lineItemRow}
                        getRowClassName={(rowIdx) =>
                            rowIdx === 0 && isItemReviewHighlighted
                                ? classNames(
                                      styles.lineItemRow,
                                      styles.reviewTaskItemRow,
                                  )
                                : styles.lineItemRow
                        }
                        onChange={handleLineChange}
                        stickyHeader
                        overflow="auto"
                        lightNotEditableCells
                        noOuterBorder
                    />
                </div>
                <button
                    type="button"
                    className={styles.addItemRow}
                    onClick={() => onAddLine()}
                >
                    Add item
                    <Shortcut keys="shift+enter" />
                </button>
                <div className={styles.totalsBlock}>
                    {!shouldShowAdjustments && (
                        <button
                            type="button"
                            className={styles.addAdjustmentsRow}
                            onClick={() => setIsAdjustmentsRevealed(true)}
                        >
                            Add tax, discount or deposit
                        </button>
                    )}
                    {shouldShowAdjustments && (
                        <>
                            <TotalsTextRow
                                label="Subtotal"
                                value={formatMoney(subtotal)}
                            />
                            <TotalsAmountRow
                                label="Tax"
                                value={tax}
                                mode={taxMode}
                                isDisabled={isTaxPerLineItem}
                                computedAmount={taxAmount}
                                computedPercent={taxPercent}
                                onChange={(value) =>
                                    setState((prev) => ({
                                        ...prev,
                                        tax: value,
                                    }))
                                }
                                onModeChange={(mode) =>
                                    handlePerLineModeChange("tax", mode)
                                }
                            />
                            <TotalsAmountRow
                                label="Discount"
                                value={discount}
                                mode={discountMode}
                                isDisabled={isDiscountPerLineItem}
                                computedAmount={discountAmount}
                                computedPercent={discountPercent}
                                onChange={(value) =>
                                    setState((prev) => ({
                                        ...prev,
                                        discount: value,
                                    }))
                                }
                                onModeChange={(mode) =>
                                    handlePerLineModeChange("discount", mode)
                                }
                            />
                            <TotalsAmountRow
                                label="Deposit"
                                value={deposit}
                                mode={depositMode}
                                isDisabled={isDepositPerLineItem}
                                computedAmount={depositAmount}
                                computedPercent={depositPercent}
                                onChange={(value) =>
                                    setState((prev) => ({
                                        ...prev,
                                        deposit: value,
                                    }))
                                }
                                onModeChange={(mode) =>
                                    handlePerLineModeChange("deposit", mode)
                                }
                            />
                        </>
                    )}
                    <TotalsTextRow
                        label="Total due"
                        value={formatMoney(total)}
                        isGrand
                    />
                </div>
            </div>
        </div>
    );
};
